import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Box from "../../hoc/Box/Box";
import Modal from "../UI/Modal/Modal";
import Success from "../UI/Success/Success";
import Error from "../UI/Error/Error";
import Spinner from "../UI/Spinner/Spinner";
import Switch from "../UI/Switch/Switch";
import * as actions from "../../store/actions/index";
import classes from "./ThreadNetworkOverviewPage.css";
import { FaSignal } from "react-icons/fa";

const COUNTDOWN_SECONDS = 60;

const ThreadNetworkOverviewPage = (props) => {
	const [countdown, setCountdown] = useState(0);
	const [pendingAction, setPendingAction] = useState(null); // 'enable' | 'disable' | null
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [showThreadNetworkToggleModal, setShowThreadNetworkToggleModal] = useState(false);
	const timerRef = useRef(null);
	const pollRef = useRef(null);
	const pendingActionRef = useRef(null);
	const onCompleteRef = useRef(null);
	const preToggleStatusRef = useRef(null);
	const isRadioEnabled = props.radioStatus?.value === 'enabled';
	const isStarting = pendingAction === 'enable' && countdown > 0;
	const isStopping = pendingAction === 'disable' && countdown > 0;

	const stopTimer = useCallback(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	}, []);

	const startCountdown = useCallback((onComplete) => {
		stopTimer();
		onCompleteRef.current = onComplete;
		setCountdown(COUNTDOWN_SECONDS);
		timerRef.current = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearInterval(timerRef.current);
					timerRef.current = null;
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, [stopTimer]);

	// Fire onComplete when countdown reaches 0
	useEffect(() => {
		if (countdown === 0 && onCompleteRef.current) {
			const cb = onCompleteRef.current;
			onCompleteRef.current = null;
			cb();
		}
	}, [countdown]);

	// Fetch radio status and WAN data on mount
	useEffect(() => {
		props.getThreadRadioStatus();
		props.onInitWan();
		return () => {
			stopTimer();
			stopPolling();
		};
	}, []);

	// If initial load failed, redirect to All Equipment page
	useEffect(() => {
		if (props.threadInitialLoadFailed || sessionStorage.getItem("userRoles")?.includes("threadNetwork") === false) {
			props.history.push('/overview');
		}
	}, [props.threadInitialLoadFailed]);

	// Handle toggle API success — start polling for status, stop timer early when confirmed
	useEffect(() => {
		if (!props.toggleSuccess) return;
		const action = pendingActionRef.current;
		props.onResetNetworkThreadErrorSuccess();
		if (action === 'enable' || action === 'disable') {
			stopPolling();
			startCountdown(() => {
				// Fallback: timer expired without early confirmation — show error
				stopPolling();
				setPendingAction(null);
				pendingActionRef.current = null;
				props.getThreadRadioStatus();
				props.getNetworkThread();
				setShowErrorModal(true);
			});
			// Check immediately, then poll every 5s
			props.getThreadRadioStatus();
			pollRef.current = setInterval(() => {
				props.getThreadRadioStatus();
			}, 5000);
		}
	}, [props.toggleSuccess]);

	// If getThreadRadioStatus fails during polling, show error modal
	useEffect(() => {
		if (!props.radioStatusFail || !pendingAction || countdown === 0 || !pollRef.current) return;
		stopTimer();
		stopPolling();
		onCompleteRef.current = null;
		setCountdown(0);
		setPendingAction(null);
		pendingActionRef.current = null;
		setShowErrorModal(true);
	}, [props.radioStatusFail, pendingAction, countdown]);

	// Stop timer early when radio status confirms the change
	useEffect(() => {
		if (!pendingAction || countdown === 0 || props.toggleFail || props.radioStatusFail) return;
		const expectedValue = pendingAction === 'enable' ? 'enabled' : 'disabled';
		// Only confirm if status actually changed from pre-toggle value
		if (props.radioStatus?.value === expectedValue && props.radioStatus?.value !== preToggleStatusRef.current) {
			stopTimer();
			stopPolling();
			onCompleteRef.current = null;
			setCountdown(0);
			setPendingAction(null);
			pendingActionRef.current = null;
			props.getNetworkThread();
			setShowSuccessModal(true);
		}
	}, [props.radioStatus, pendingAction, countdown, props.toggleFail, props.radioStatusFail]);

	// Handle toggle API failure
	useEffect(() => {
		if (!props.toggleFail) return;
		stopTimer();
		stopPolling();
		onCompleteRef.current = null;
		setCountdown(0);
		setPendingAction(null);
		pendingActionRef.current = null;
		setShowErrorModal(true);
	}, [props.toggleFail]);

	const handleToggle = () => {
		setShowThreadNetworkToggleModal(true)
	};

	const handleConfirmThreadNetworkToggleModal = () => {
		const newValue = !isRadioEnabled;
		const action = newValue ? 'enable' : 'disable';
		preToggleStatusRef.current = props.radioStatus?.value;
		pendingActionRef.current = action;
		setPendingAction(action);
		props.toggleNetworkThread(newValue);
		setShowThreadNetworkToggleModal(false);
	};

	const handleCancelThreadNetworkToggleModal = () => {
		setShowThreadNetworkToggleModal(false);
	};

	const formatTime = (seconds) => {
		const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
		const secs = String(seconds % 60).padStart(2, '0');
		return `${mins}:${secs}`;
	};

	const closeSuccessModal = () => {
		setShowSuccessModal(false);
		props.onResetNetworkThreadErrorSuccess();
	};

	const closeErrorModal = () => {
		setShowErrorModal(false);
		props.onResetNetworkThreadErrorSuccess();
	};

	if (props.loading) {
		return <Spinner />;
	}

	return (
		<Box>
			<div className={classes.MainContent}>
				<div className={classes.MainContentContainer}>
					<div className={classes.ThreadCard}>
						<div className={classes.ThreadCardHeader}>
							<div className={classes.ThreadCardHeaderLeft}>
								<span className={classes.ThreadCardTitle}>Thread Network</span>
								<span className={isRadioEnabled ? classes.BadgeBroadcasting : classes.BadgeNotBroadcasting}>
									<FaSignal style={{ marginRight: '5px', verticalAlign: 'middle', fontSize: '12px' }} />
									{isRadioEnabled ? 'Broadcasting' : 'Not Broadcasting'}
								</span>
							</div>
							<div className={classes.ThreadCardHeaderRight}>
								<Switch
									checked={(isRadioEnabled && !isStopping) || isStarting}
									onChange={handleToggle}
									disabled={isStarting || isStopping || props.toggleLoading || props.realWan?.connectionState === "disconnected"}
								/>
							</div>
						</div>
						<div className={classes.ThreadCardBody}>
							{isStarting ? (
								<div className={classes.StartingContainer}>
									<div className={classes.SpinnerIcon} />
									<div className={classes.StartingText}>Starting Thread Network.....</div>
									<div className={classes.CountdownText}>{formatTime(countdown)} remaining</div>
								</div>
							) : isStopping ? (
								<div className={classes.StartingContainer}>
									<div className={classes.SpinnerIcon} />
									<div className={classes.StartingText}>Shutting down Thread Network .....</div>
									<div className={classes.CountdownText}>{formatTime(countdown)} remaining</div>
								</div>
							) : (
								<p>Thread is a smart home network broadcast from the router that is used to connect certain smart home devices.</p>
							)}
						</div>
					</div>
				</div>
			</div>
			<Modal show={showSuccessModal} modalClosed={closeSuccessModal}>
				<Success id="ThreadToggleSuccess" message="Success" />
			</Modal>
			<Modal show={showErrorModal} modalClosed={closeErrorModal}>
				<Error id="ThreadToggleError" />
			</Modal>
			<Modal show={showThreadNetworkToggleModal} modalClosed={handleCancelThreadNetworkToggleModal}>
				<div className={classes.ConfirmModalContent}>
					<p className={classes.ConfirmModalText}>
						{
							isRadioEnabled
							? "This will disconnect customer's smart home devices. Disable Thread Network?"
							: "Thread network is now active. Devices with valid credentials will reconnect shortly. Devices that fail to reconnect may need re-commissioning."
						}
					</p>
					<div className={classes.ConfirmModalButtons}>
						<button className={classes.CancelButton} onClick={handleCancelThreadNetworkToggleModal}>Cancel</button>
						<button className={classes.OkButton} onClick={handleConfirmThreadNetworkToggleModal}>OK</button>
					</div>
				</div>
			</Modal>
		</Box>
	);
};

const mapStateToProps = (state) => ({
	networkThread: state.networkThread.networkThread,
	loading: state.networkThread.networkThreadLoading,
	error: state.networkThread.networkThreadFail,
	radioStatus: state.networkThread.radioStatus,
	radioStatusLoading: state.networkThread.radioStatusLoading,
	radioStatusFail: state.networkThread.radioStatusFail,
	toggleLoading: state.networkThread.toggleLoading,
	toggleSuccess: state.networkThread.toggleSuccess,
	toggleFail: state.networkThread.toggleFail,
	threadInitialLoadFailed: state.networkThread.threadInitialLoadFailed,
	selectedEquipment: state.equipment.selectedEquipment,
	realWan: state.wan.realWan,
});

const mapDispatchToProps = (dispatch) => ({
	getNetworkThread: () => dispatch(actions.getNetworkThread()),
	getThreadRadioStatus: () => dispatch(actions.getThreadRadioStatus()),
	toggleNetworkThread: (enabled) => dispatch(actions.toggleNetworkThread(enabled)),
	onResetNetworkThreadErrorSuccess: () => dispatch(actions.resetNetworkThreadErrorSuccess()),
	onInitWan: () => dispatch(actions.initWan()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ThreadNetworkOverviewPage));
